import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { LanguageProvider, useLanguage } from '../context/LanguageContext';
import AnnouncementBar from '../components/layout/AnnouncementBar';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import StickyMobileCTA from '../components/layout/StickyMobileCTA';
import BookingModal from '../components/booking/BookingModal';
import DynamicSection from '../components/sections/DynamicSection';
import { Toaster } from '../components/ui/sonner';
import { Home, AlertCircle, Loader2 } from 'lucide-react';

const DynamicPage = ({ onRegisterClick }) => {
  const { slug } = useParams();
  const { isRTL } = useLanguage();
  const [page, setPage] = useState(null);
  const [sections, setSections] = useState([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [sectionsLoading, setSectionsLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    if (!slug) return;
    setPageLoading(true);
    setNotFound(false);
    setPage(null);
    setSections([]);
    setSectionsLoading(false);

    const fetchPage = async () => {
      try {
        const response = await fetch(`${backendUrl}/api/cms/pages/${slug}`);
        if (response.status === 404) {
          setNotFound(true);
          setPageLoading(false);
          return;
        }
        if (!response.ok) {
          setNotFound(true);
          setPageLoading(false);
          return;
        }
        const data = await response.json();
        setPage(data);
        setPageLoading(false);
        document.title = `${data.title} — Mona Bizzari`;

        if (data.id) {
          setSectionsLoading(true);
          fetch(`${backendUrl}/api/cms/sections?page_id=${data.id}`)
            .then(r => r.ok ? r.json() : [])
            .then(secData => {
              const list = Array.isArray(secData) ? secData : [];
              list.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
              setSections(list);
            })
            .catch(err => {
              console.error('DynamicPage: sections fetch error', err);
              setSections([]);
            })
            .finally(() => setSectionsLoading(false));
        }
      } catch (err) {
        console.error('DynamicPage: fetch error', err);
        setNotFound(true);
        setPageLoading(false);
      }
    };

    fetchPage();

    return () => { document.title = 'Mona Bizzari'; };
  }, [slug, backendUrl]);

  if (pageLoading) {
    return (
      <div className="min-h-screen bg-charcoal flex items-center justify-center">
        <Loader2 className="text-gold animate-spin" size={36} />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-charcoal flex flex-col items-center justify-center gap-6 px-6 text-center">
        <AlertCircle size={48} className="text-gold/60" />
        <h1 className="font-serif text-4xl sm:text-5xl text-cream">
          {isRTL ? 'الصفحة غير موجودة' : 'Page Not Found'}
        </h1>
        <p className="text-cream-400 max-w-sm">
          {isRTL
            ? 'عذراً، الصفحة التي تبحث عنها غير متاحة.'
            : "Sorry, the page you're looking for is not available."}
        </p>
        <Link
          to="/"
          className="flex items-center gap-2 px-6 py-3 bg-gold text-charcoal text-sm uppercase tracking-wider font-semibold rounded-lg hover:bg-gold/90 transition-colors"
        >
          <Home size={16} />
          {isRTL ? 'العودة للرئيسية' : 'Back to Home'}
        </Link>
      </div>
    );
  }

  const titleAr = page.title_ar || page.meta?.title_ar || '';
  const title = isRTL ? (titleAr || page.title) : page.title;
  const body = isRTL
    ? (page.content_ar || page.content || '')
    : (page.content || '');

  return (
    <div className="min-h-screen bg-charcoal">
      <AnnouncementBar />
      <Header onRegisterClick={onRegisterClick} />

      <main className="pt-40 pb-24 px-6 lg:px-10 max-w-4xl mx-auto">
        <div className="mb-10">
          <Link
            to="/"
            className="text-cream-400 hover:text-gold transition-colors text-xs uppercase tracking-wider"
          >
            {isRTL ? 'الرئيسية' : 'Home'}
          </Link>
          <span className="text-cream-400/40 mx-2">/</span>
          <span className="text-gold text-xs uppercase tracking-wider">{title}</span>
        </div>

        <div className="gold-divider mb-10" />

        <h1 className={`font-serif text-4xl sm:text-5xl lg:text-6xl text-cream mb-8 leading-tight ${isRTL ? 'text-right' : 'text-left'}`}>
          {title}
        </h1>

        {body ? (
          <div
            className={`page-content ${isRTL ? 'text-right' : 'text-left'}`}
            dangerouslySetInnerHTML={{ __html: body }}
          />
        ) : (
          !sectionsLoading && sections.length === 0 && (
            <p className="text-cream-400/50 italic">
              {isRTL ? 'لا يوجد محتوى لهذه الصفحة بعد.' : 'No content yet for this page.'}
            </p>
          )
        )}

        {sections.length > 0 && <div className="gold-divider mt-12 mb-4" />}
      </main>

      {sectionsLoading && (
        <div className="flex justify-center py-12">
          <Loader2 className="text-gold/50 animate-spin" size={24} />
        </div>
      )}

      {sections.length > 0 && (
        <div>
          {sections.map((section, i) => (
            <React.Fragment key={section.id || i}>
              <DynamicSection section={section} index={i} />
              {i < sections.length - 1 && (
                <div className="gold-divider my-0" />
              )}
            </React.Fragment>
          ))}
        </div>
      )}

      <Footer />
    </div>
  );
};

const DynamicPageInner = () => {
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [bookingSessionType, setBookingSessionType] = useState('');

  const openBooking = (sessionType = '') => {
    setBookingSessionType(sessionType);
    setIsBookingOpen(true);
  };

  return (
    <>
      <DynamicPage onRegisterClick={() => openBooking()} />
      <StickyMobileCTA onBookClick={() => openBooking()} />
      <BookingModal
        isOpen={isBookingOpen}
        onClose={() => setIsBookingOpen(false)}
        sessionType={bookingSessionType}
        sourcePage={window.location.pathname}
      />
      <Toaster position="top-center" />
    </>
  );
};

const DynamicPageWrapper = () => (
  <LanguageProvider>
    <DynamicPageInner />
  </LanguageProvider>
);

export default DynamicPageWrapper;
